import { Link } from "react-router-dom";
import { Check, Lock, Clock, ArrowRight, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { abTrack, catLabel } from "@/lib/ab";

export const isChecking = (r) => r?.payment_status === "pending" && (r.proof_submitted || r.whatsapp_sent);
export const useLang = () => (useAuth().user?.language === "fr" ? "fr" : "en");
export const clickUpgrade = (lang, from) => abTrack("upgrade_clicked", { lang, props: { from } });

const C = {
  en: {
    checkT: "We’re checking your payment", checkD: "Thank you! We will check your payment and unlock all ideas for you. You don’t need to pay again.",
    rejected: "We could not confirm your last payment. Please check and send your proof again.",
    upT: "Want to discover more business ideas?", upD: "Unlock more than 150 business ideas with the budgets, target customers and steps you need to get started.",
    benefits: ["Access all 150+ business ideas", "Learn how each business works", "See estimated startup costs", "Discover ways to find customers", "Follow simple steps to get started", "Pay once for access to the complete ideas guide"],
    once: "– One-time payment", cta: "Unlock 150+ Ideas", lockedCta: "Unlock This Idea",
  },
  fr: {
    checkT: "Nous vérifions ton paiement", checkD: "Merci ! Nous vérifions ton paiement et nous débloquons toutes les idées pour toi. Tu n’as pas besoin de payer encore.",
    rejected: "Nous n’avons pas pu confirmer ton dernier paiement. Vérifie et envoie ta preuve à nouveau.",
    upT: "Tu veux découvrir d’autres idées de business ?", upD: "Débloque plus de 150 idées de business avec les budgets, les clients à cibler et les étapes pour te lancer.",
    benefits: ["Accès aux 150+ idées de business", "Comprends comment marche chaque business", "Vois le budget estimé pour démarrer", "Découvre comment trouver des clients", "Suis des étapes simples pour te lancer", "Paie une seule fois pour le guide complet"],
    once: "– Paiement unique", cta: "Débloquer les 150+ idées", lockedCta: "Débloquer cette idée",
  },
};
export const useCardText = () => C[useLang()];

export function CheckingCard() {
  const c = useCardText();
  return (
    <div className="rounded-3xl bg-amber-50 border border-amber-200 p-5" data-testid="free-payment-checking">
      <div className="flex items-center gap-3"><Clock className="h-7 w-7 text-amber-600 shrink-0" /><div className="font-display font-extrabold text-lg text-[#102B3F]">{c.checkT}</div></div>
      <p className="text-[15px] text-[#5C4A1A] mt-2">{c.checkD}</p>
    </div>
  );
}

export function UpgradeCard({ price, rejected }) {
  const lang = useLang();
  const c = C[lang];
  return (
    <div id="free-upgrade-section" className="rounded-3xl bg-[#102B3F] text-white p-5 sm:p-6" data-testid="free-upgrade-section">
      {rejected && <div className="flex gap-2 items-start rounded-2xl bg-rose-500/15 text-rose-100 p-3 mb-4 text-sm" data-testid="free-payment-rejected"><XCircle className="h-5 w-5 shrink-0" />{c.rejected}</div>}
      <h2 className="font-display font-extrabold text-2xl" data-testid="free-upgrade-title">{c.upT}</h2>
      <p className="text-white/80 text-[15px] mt-1.5">{c.upD}</p>
      <ul className="mt-4 space-y-2">{c.benefits.map((b) => <li key={b} className="flex gap-2.5 text-[15px]"><Check className="h-5 w-5 text-[#0BAF9A] shrink-0" strokeWidth={3} />{b}</li>)}</ul>
      <div className="mt-5 flex items-baseline gap-2"><span className="font-display font-extrabold text-3xl" data-testid="free-upgrade-price">Rs {price}</span><span className="text-sm text-white/70">{c.once}</span></div>
      <Link to="/unlock" onClick={() => clickUpgrade(lang, "upgrade_section")} className="lp-btn w-full mt-4" data-testid="free-unlock-all-btn">{c.cta} <ArrowRight className="h-5 w-5" /></Link>
    </div>
  );
}

export function LockedCard({ idea, onClick }) {
  const lang = useLang();
  const c = C[lang];
  return (
    <button onClick={onClick} className="relative text-left rounded-2xl bg-white border border-[#E1ECEF] p-4 overflow-hidden hover:border-[#0BAF9A] transition-colors" data-testid={`locked-idea-${idea.number}`}>
      <div className="text-[11px] font-semibold text-[#7D8C96]">{catLabel(idea.category, lang)}</div>
      <div className="font-display font-bold text-[#102B3F] leading-tight mt-1">{idea.name}</div>
      <div className="mt-3 space-y-1.5 blur-[3px] select-none" aria-hidden>
        <div className="h-2.5 rounded bg-[#D5E3E8] w-11/12" /><div className="h-2.5 rounded bg-[#D5E3E8] w-8/12" /><div className="h-2.5 rounded bg-[#D5E3E8] w-10/12" />
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-sm font-bold text-[#088F7E]"><Lock className="h-4 w-4" />{c.lockedCta}</div>
    </button>
  );
}
